import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import { Heading } from '../../components';

import { formatListingFieldValue, getListingFieldEntry } from './listingFieldGroups';
import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

const IconDelivery = () => (
  <svg className={css.infoCardIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M3 7.5h10v9H3zM13 10.5h4l4 3.5v2.5h-8z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="7" cy="18" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="17.5" cy="18" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const IconReturns = () => (
  <svg className={css.infoCardIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M20 12a8 8 0 1 1-2.6-5.9M20 4v4h-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoCard = props => {
  const { icon, title, text } = props;
  return (
    <div className={css.infoCard}>
      <span className={css.infoCardIconWrapper}>{icon}</span>
      <div>
        <Heading as="h3" rootClassName={css.infoCardTitle}>
          {title}
        </Heading>
        <p className={css.infoCardText}>
          {richText(text, {
            linkify: true,
            longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            longWordClass: css.longWord,
          })}
        </p>
      </div>
    </div>
  );
};

/**
 * The practical detail a shopper wants before buying: when the order ships and
 * what happens if they change their mind. Both come from hosted listing fields
 * that sellers fill in while listing, so nothing is shown unless a seller has
 * actually said something.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.deliveryFieldConfig] hosted config of the delivery field
 * @param {Object} [props.returnsFieldConfig] hosted config of the returns field
 * @param {Object} props.publicData listing.attributes.publicData
 * @param {Object} props.metadata listing.attributes.metadata
 * @returns {JSX.Element|null} null when the seller filled in neither field
 */
const SectionDeliveryReturns = props => {
  const { deliveryFieldConfig, returnsFieldConfig, publicData, metadata } = props;

  const delivery = getListingFieldEntry(deliveryFieldConfig, publicData, metadata);
  const returns = getListingFieldEntry(returnsFieldConfig, publicData, metadata);

  if (!delivery && !returns) {
    return null;
  }

  return (
    <section className={css.sectionDeliveryReturns}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.deliveryAndReturnsTitle" />
      </Heading>

      <div className={css.infoCards}>
        {delivery ? (
          <InfoCard
            icon={<IconDelivery />}
            title={delivery.label || <FormattedMessage id="ListingPage.deliveryFallbackLabel" />}
            text={formatListingFieldValue(delivery.value, deliveryFieldConfig)}
          />
        ) : null}
        {returns ? (
          <InfoCard
            icon={<IconReturns />}
            title={returns.label || <FormattedMessage id="ListingPage.returnsFallbackLabel" />}
            text={formatListingFieldValue(returns.value, returnsFieldConfig)}
          />
        ) : null}
      </div>
    </section>
  );
};

export default SectionDeliveryReturns;
